import { z } from "zod";
import { Category, CategoryKind } from "../domain.js";
import { Id, IsoMonth, Pence } from "../primitives.js";
import { RangePeriod, ResolvedPeriod } from "./query.js";

/**
 * Categories are rows, not the free text phase 1 stored on every transaction.
 * A string on 473 transactions cannot be renamed, coloured, reordered or
 * archived without rewriting the ledger.
 *
 * One level. They were a two-level tree until migration 0005, where the parent
 * level turned out to hold the account name and nothing else — so "Food, on
 * the joint account" is a filter on the account, not a second category above
 * the first.
 */

export const CategoryQuery = z
  .object({
    /** Totals are period-scoped; without either they cover all of time. */
    month: IsoMonth.optional(),
    period: RangePeriod.optional(),
    includeArchived: z.coerce.boolean().optional(),
  })
  .refine((query) => !(query.month !== undefined && query.period !== undefined), {
    message: "Give a month or a period, not both.",
  });

/**
 * `totalPence` and `periodTxnCount` both describe the period the list was
 * asked for, and describe the same rows as each other. `txnCount`, inherited
 * from the category itself, is every transaction ever filed under it — which
 * is what a delete or a merge acts on, and so what those say out loud.
 *
 * `totalPence` is signed: negative for money out, positive for money in, and
 * around zero for a transfer category whose two halves are both here.
 */
export const CategoryWithTotal = Category.extend({
  totalPence: Pence,
  periodTxnCount: z.number().int(),
});

export const CategoryList = z.object({
  data: z.array(CategoryWithTotal),
  /** The period totals were resolved against; null when none was given. */
  period: ResolvedPeriod.nullable(),
});

export const CategoryCreate = z.object({
  name: z.string().trim().min(1).max(60),
  kind: CategoryKind.default("spending"),
  colour: z.string().max(40).nullable().optional(),
  icon: z.string().max(40).nullable().optional(),
});

export const CategoryPatch = z
  .object({
    name: z.string().trim().min(1).max(60),
    kind: CategoryKind,
    colour: z.string().max(40).nullable(),
    icon: z.string().max(40).nullable(),
    sortOrder: z.number().int(),
    essential: z.boolean(),
  })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, "Nothing to change.");

/**
 * Deleting a category that is in use is a 409 unless the caller says where the
 * transactions should go — because the alternative is a ledger with a dangling
 * reference and a month whose totals quietly changed.
 */
export const CategoryDeleteQuery = z.object({
  reassignTo: Id.optional(),
});

/**
 * Merging folds several categories into one that survives. It is the answer to
 * "these two are the same thing" — Petrol and Travel — which a rename cannot
 * give, because the name is unique and the second one is already taken.
 *
 * The survivor is the subject: it is named in the path, and everything that
 * pointed at a source ends up pointing at it. So the counts that come back are
 * about what moved, which is what the confirmation has to be able to say.
 */
export const CategoryMerge = z.object({
  sourceIds: z.array(Id).min(1).max(100),
});

export const CategoryMergeResult = z.object({
  category: Category,
  mergedCount: z.number().int().nonnegative(),
  moved: z.number().int().nonnegative(),
  rulesMoved: z.number().int().nonnegative(),
  budgetsMoved: z.number().int().nonnegative(),
});

export type CategoryQuery = z.infer<typeof CategoryQuery>;
export type CategoryWithTotal = z.infer<typeof CategoryWithTotal>;
export type CategoryList = z.infer<typeof CategoryList>;
export type CategoryCreate = z.infer<typeof CategoryCreate>;
export type CategoryPatch = z.infer<typeof CategoryPatch>;
export type CategoryDeleteQuery = z.infer<typeof CategoryDeleteQuery>;
export type CategoryMerge = z.infer<typeof CategoryMerge>;
export type CategoryMergeResult = z.infer<typeof CategoryMergeResult>;
